import {
  Injectable,
  inject,
  PLATFORM_ID,
  DestroyRef,
  signal,
  computed,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AppStorageService } from './app-storage.service';

/**
 * Sound notification service (GAP-012).
 *
 * Plays short audio cues for high-signal events:
 *   - `fill`            — an order filled.
 *   - `halt`            — kill switch armed / strategy halted.
 *   - `alert`           — risk alert / SLA breach.
 *   - `queue-warning`   — approval queue approaching SLA.
 *
 * Sounds are MUTED by default. Operators opt-in per event from settings.
 * Master mute and per-event toggles persist to localStorage.
 *
 * Audio files are NOT bundled by default — wire them up by placing
 * preloadable WebAudio buffers under `assets/sounds/` and updating
 * `SOUND_MANIFEST` below. The service stays a no-op until then.
 *
 * Accessibility: there is no "prefers-reduced-sound" media query;
 * silence is the default, and operator opt-in is explicit.
 */
export type SoundCue = 'fill' | 'halt' | 'alert' | 'queue-warning';

interface SoundManifest { readonly [cue: string]: { readonly src: string } }

/**
 * SOUND_MANIFEST is intentionally empty in the current build. The
 * OscillatorNode fallback (GAP-FU-13) covers every cue in SoundCue with
 * synthesized tones, so adding real .mp3 / .ogg assets is purely a
 * quality-of-life enhancement — track as a separate asset-bundle GAP
 * when audio design lands. Empty manifest is the supported steady state.
 */
const SOUND_MANIFEST: SoundManifest = {
  // 'fill':          { src: 'assets/sounds/fill.mp3' },
  // 'halt':          { src: 'assets/sounds/halt.mp3' },
  // 'alert':         { src: 'assets/sounds/alert.mp3' },
  // 'queue-warning': { src: 'assets/sounds/queue-warning.mp3' },
};

const STORAGE_KEY = 'razor:sounds';

interface Preferences {
  readonly muted: boolean;
  readonly volume: number; // 0-1
  readonly enabled: Record<SoundCue, boolean>;
}

const DEFAULT_PREFS: Preferences = {
  muted: true,
  volume: 0.6,
  enabled: {
    fill: false,
    halt: false,
    alert: false,
    'queue-warning': false,
  },
};

@Injectable({ providedIn: 'root' })
export class SoundService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly appStorage = inject(AppStorageService);

  private readonly _prefs = signal<Preferences>(DEFAULT_PREFS);
  readonly prefs = this._prefs.asReadonly();

  readonly isMuted = computed(() => this._prefs().muted);
  readonly volume = computed(() => this._prefs().volume);

  private context: AudioContext | null = null;
  private readonly buffers = new Map<SoundCue, AudioBuffer>();

  constructor() {
    this.loadPrefs();
  }

  setMuted(muted: boolean): void {
    this._prefs.update(p => ({ ...p, muted }));
    this.persist();
  }

  setVolume(volume: number): void {
    const clamped = Math.max(0, Math.min(1, volume));
    this._prefs.update(p => ({ ...p, volume: clamped }));
    this.persist();
  }

  setCueEnabled(cue: SoundCue, enabled: boolean): void {
    this._prefs.update(p => ({
      ...p,
      enabled: { ...p.enabled, [cue]: enabled },
    }));
    this.persist();
  }

  /**
   * Fire a sound. No-op if muted or the cue is disabled. If no bundled
   * asset exists for the cue, falls back to an OscillatorNode-synthesised
   * tone (see GAP-FU-13 / src/assets/sounds/README.md) so cues already
   * function during development.
   */
  play(cue: SoundCue): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const prefs = this._prefs();
    if (prefs.muted) return;
    if (!prefs.enabled[cue]) return;
    const manifest = SOUND_MANIFEST[cue];
    if (manifest) {
      void this.playInternal(cue, manifest.src, prefs.volume);
    } else {
      this.playOscillatorFallback(cue, prefs.volume);
    }
  }

  /**
   * Play a cue bypassing the SoundService preference gates (`muted`,
   * `enabled[cue]`). Intended for callers that own their own preference
   * logic and only delegate the audio-playback primitive to this service —
   * see GAP-FU-13-CONSOLIDATE (`NotificationService` migration from inline
   * `AudioContext` to this entry point).
   *
   * `volume` (0-1) overrides the user-pref volume. If omitted, falls back
   * to `prefs.volume`. The internal envelope still applies (`volume * 0.4`
   * peak with linear attack + exponential decay) so callers that want to
   * match a legacy constant-gain peak `g` should pass `g / 0.4`.
   */
  playRaw(cue: SoundCue, volume?: number): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const v = Math.max(0, Math.min(1, volume ?? this._prefs().volume));
    const manifest = SOUND_MANIFEST[cue];
    if (manifest) {
      void this.playInternal(cue, manifest.src, v);
    } else {
      this.playOscillatorFallback(cue, v);
    }
  }

  /**
   * GAP-FU-13 · OscillatorNode tone synthesised in-browser. Used until
   * proper assets land under assets/sounds/.
   */
  private playOscillatorFallback(cue: SoundCue, volume: number): void {
    const ctx = this.ensureContext();
    if (!ctx) return;
    // Per-cue tone shape — see assets/sounds/README.md for production targets.
    const shape: Record<SoundCue, { freq: number; type: OscillatorType; duration: number }> = {
      'fill':          { freq: 660, type: 'sine',     duration: 0.16 },
      'halt':          { freq: 220, type: 'square',   duration: 0.45 },
      'alert':         { freq: 880, type: 'triangle', duration: 0.20 },
      'queue-warning': { freq: 440, type: 'sawtooth', duration: 0.22 },
    };
    const cfg = shape[cue];
    const osc = ctx.createOscillator();
    osc.type = cfg.type;
    osc.frequency.value = cfg.freq;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.gain.linearRampToValueAtTime(volume * 0.4, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + cfg.duration);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + cfg.duration);
  }

  private async playInternal(cue: SoundCue, src: string, volume: number): Promise<void> {
    try {
      const ctx = this.ensureContext();
      if (!ctx) return;
      const buffer = this.buffers.get(cue) ?? await this.loadBuffer(src, ctx);
      this.buffers.set(cue, buffer);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      const gain = ctx.createGain();
      gain.gain.value = volume;
      source.connect(gain).connect(ctx.destination);
      source.start();
    } catch {
      // Asset missing / CORS / decode failure — silently swallow.
    }
  }

  private ensureContext(): AudioContext | null {
    if (this.context) return this.context;
    if (typeof AudioContext === 'undefined') return null;
    this.context = new AudioContext();
    this.destroyRef.onDestroy(() => void this.context?.close());
    return this.context;
  }

  private async loadBuffer(src: string, ctx: AudioContext): Promise<AudioBuffer> {
    const response = await fetch(src);
    const arrayBuffer = await response.arrayBuffer();
    return await ctx.decodeAudioData(arrayBuffer);
  }

  private loadPrefs(): void {
    // GAP-FE-C013: SSR-safe, quota-resilient wrapper; preserves STORAGE_KEY and
    // the partial-Preferences merge (getJSON returns null on server/miss/corrupt).
    const parsed = this.appStorage.getJSON<Partial<Preferences>>(STORAGE_KEY);
    if (!parsed) return;
    this._prefs.set({
      muted: parsed.muted ?? DEFAULT_PREFS.muted,
      volume: parsed.volume ?? DEFAULT_PREFS.volume,
      enabled: { ...DEFAULT_PREFS.enabled, ...(parsed.enabled ?? {}) },
    });
  }

  private persist(): void {
    // GAP-FE-C013: SSR-safe, quota-resilient wrapper; preserves the STORAGE_KEY key.
    this.appStorage.setJSON(STORAGE_KEY, this._prefs());
  }
}
